<template>
  <div class="sc-page-root" :class="{ 'sc-page-root--embedded': embedded }">
    <FiPageRail v-if="!embedded" title="Compare Drivers" nowrap-actions>
      <template #actions>
        <driver-manager-compare-help />
        <b-button v-if="results_match_selections" size="sm" variant="outline-secondary"
                  @click="downloading_csv ? null : download_csv()" :disabled="downloading_csv"
                  :title="downloading_csv ? 'Preparing CSV…' : 'Download CSV'"
                  :aria-label="downloading_csv ? 'Preparing CSV' : 'Download CSV'">
          <i class="fa" :class="downloading_csv ? 'fa-spinner fa-spin' : 'fa-download'"></i>
        </b-button>
      </template>
    </FiPageRail>

    <div class="sc-toolbar">
      <template v-if="!embedded">
        <div class="sc-toolbar-group dsd-date-presets" role="group" aria-label="Date range">
          <button v-for="m in quickRangeOptions" :key="'qr-' + m" type="button"
            :class="['sc-chip', { 'sc-chip--active': activeQuickRangeMonths === m }]"
            :aria-pressed="activeQuickRangeMonths === m ? 'true' : 'false'"
            @click="setDatePreset(m)">{{ m }}m</button>
        </div>
        <div class="dsd-date-fields">
          <date-picker class="sc-control dsd-date-field" v-model="details_fromDate" :config="optionsFrom" :placeholder="details_fromDate"/>
          <span class="dsd-date-sep">to</span>
          <date-picker class="sc-control dsd-date-field" v-model="details_toDate" :config="optionsTo" :placeholder="details_toDate"/>
        </div>
      </template>
      <div v-else class="sc-toolbar-group">
        <span>{{ details_fromDate }} to {{ details_toDate }}</span>
      </div>
    </div>

    <!-- Status stays the one filter above the board: it narrows the driver list
         every card's dropdown is built from (see filtered_driver_options). The
         v-if is on the row, not just the select, so a tenant with no employment
         statuses doesn't get a stranded "Status:" label. -->
    <div v-if="status_filter_options.length > 1" class="driver-picker-strip">
      <div class="driver-picker-status">
        <span style="font-weight: bold; font-size: 1.1rem;">Status:</span>&nbsp;
        <b-form-select v-model="selected_status_filter" :options="status_filter_options" class="sc-control" style="font-size: 14px; width:160px"/>
      </div>
    </div>

    <!-- Driver picker board: one wide, tall card per driver slot (two by
         default) filling the page width, with a narrow "+" column pinned to the
         right that adds a slot -- three cards split the board into thirds, four
         into quarters, and so on. -->
    <div v-if="driver_options.length === 0" class="dm-board-loading">
      <b-spinner style="width: 2rem; height: 2rem;" label="Loading..."></b-spinner>
    </div>

    <template v-else>
      <p class="comparison-scroll-hint">⟷ Scroll horizontally to see all drivers</p>

      <!-- One table for the whole page: the driver dropdowns are the column
           headers, sitting directly above the values they govern. It renders at
           its final shape from the first paint -- every metric row, a fixed
           label column, and grayed sample values in any column without a driver
           -- so picking a driver swaps values in place and never reflows the
           table. table-layout: fixed is what holds that: column widths come from
           the colgroup, not from whatever text happens to be in the cells. -->
      <div class="comparison-table-wrap">
        <table class="comparison-table dm-board-table" :style="{ minWidth: table_min_width }">
          <colgroup>
            <col class="dm-col-labels"/>
            <col v-for="(col, idx) in display_columns" :key="'cg-' + idx"/>
            <col class="dm-col-add"/>
          </colgroup>
          <thead>
            <tr>
              <th class="comparison-label-col dm-head-cell"></th>
              <th v-for="(col, idx) in display_columns" :key="'hdr-' + idx"
                class="comparison-driver-col dm-head-cell"
                :class="{ 'fleet-avg-col': col.driver_id === '__fleet_avg__' }">
                <div class="dm-col-head">
                  <div class="dm-col-index">
                    <span>Driver {{ idx + 1 }}</span>
                    <button v-if="driver_selections.length > min_driver_slots" type="button"
                      class="dm-col-remove" :title="'Remove driver ' + (idx + 1)"
                      :aria-label="'Remove driver ' + (idx + 1)"
                      v-on:click.prevent="remove_driver(idx)">&times;</button>
                  </div>
                  <b-form-select
                    v-model="driver_selections[idx]"
                    :options="[{ value: null, text: 'Select Driver' }, ...filtered_driver_options]"
                    class="scorecard-driver-select"
                    v-on:change="fetch_scorecard($event)"
                  />
                  <!-- always rendered, even when it has nothing to say: an
                       appearing/disappearing hint would change the header's
                       height and shift every row under it -->
                  <div class="dm-col-hint">
                    <template v-if="col.state === 'loading'">
                      <b-spinner small label="Loading..."></b-spinner>&nbsp;Loading stats...
                    </template>
                    <template v-else-if="col.state === 'failed'">
                      <span class="text-danger">Couldn't load.</span>
                      <a href="#" class="dm-col-retry" v-on:click.prevent="retry_scorecard(col.driver_id)">Retry</a>
                    </template>
                    <template v-else-if="col.state === 'sample'">
                      Select a driver · {{ available_driver_count }} available
                    </template>
                    <template v-else>&nbsp;</template>
                  </div>
                </div>
              </th>
              <th class="dm-add-col">
                <button type="button" class="dm-add-btn" title="Add another driver to compare"
                  aria-label="Add another driver to compare" v-on:click.prevent="add_driver()">+</button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in metric_rows" :key="row.key" :class="{ 'section-header': row.section }">
              <td class="comparison-label-cell" :class="{ 'section-label': row.section }">{{ row.label }}</td>
              <td v-for="(col, idx) in display_columns" :key="'val-' + idx + '-' + row.key"
                class="comparison-value-cell"
                :class="{ 'fleet-avg-col': col.driver_id === '__fleet_avg__', 'dm-cell-muted': !row.section && col.state !== 'loaded' }">
                <template v-if="!row.section">
                  <!-- cssClass only ever colours real numbers; a sample tinted
                       green or red would read as a verdict on nobody -->
                  <span v-if="col.state === 'loaded'" :class="row.cssClass ? row.cssClass(col.data) : ''">{{ row.format(col.data) }}</span>
                  <span v-else-if="col.state === 'sample'">{{ row.format(col.data) }}</span>
                  <span v-else>—</span>
                </template>
              </td>
              <td class="dm-add-col"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex"
import axios from "axios"
import { justDate } from "../helpers/dates"
import moment from "moment"
import { tbn_api_url, get_driver_scorecard, get_fleet_scorecard, tbn_get_drivers_scorecard_list, tbn_clear_scorecard_driver_list_cache } from "../networking/tbn_apis"
import { api_url as cm_api_url, cm_get_driver_scorecard, cm_get_fleet_scorecard, cm_get_drivers_scorecard_list, cm_clear_scorecard_driver_list_cache } from "../networking/apis"
import { mergeThresholds, getFieldClass, getCompiledScoreComparisonClass, tierClass } from "../utils/driver_manager_thresholds"
import { tenantScorecardHasKudos } from "../utils/erm_access"
import {
  QUICK_RANGE_MONTHS,
  activeQuickRangeMonths,
  defaultScorecardRange,
  normalizeRange,
  quickRangeDates,
  scorecardPickerOptions,
} from '../utils/driver_manager_dates'
import dtPickerPortal from "../mixins/dtpicker_portal_mixin"

// FI-2064: quick date-range chips (1m/3m/6m/12m + Custom), matching the same
// picker used on driver_manager_dashboard.vue and driver_manager_individual.vue.

// The picker always shows at least two driver slots -- a comparison of one
// driver isn't a comparison, and the two-up board is the page's resting state.
const MIN_DRIVER_SLOTS = 2

// Column widths, shared between the colgroup CSS and table_min_width().
const LABEL_COL_PX = 180
const ADD_COL_PX = 56
const MIN_DRIVER_COL_PX = 200

// Two placeholder scorecards. They are shaped like a real `results` payload so
// an unfilled column can be rendered by the very same metric_rows formatters the
// real columns use -- which is what guarantees the table is already at its final
// shape before any driver is picked, and that selecting one only swaps values.
// Deliberately illustrative, not real: every cell they produce is greyed and
// uncoloured, and each column's header reads "Select a driver" until it holds one.
const SAMPLE_SCORECARDS = [
  {
    compiled_score: 82, compiled_score_tier: 'Solid',
    employment_status: 'Active', date_employment_start: '2019-04-15',
    miles_driven: 12480.4, date_range_days: 90,
    risk_score: 87, risk_score_rank: 14,
    speeding_percentage: 1.42, distracted_driving_percentage: 0.9,
    following_distance_per_10k_miles: 3.2, harsh_braking_per_10k_miles: 2.1,
    idling_percentage: 9.6, idling_rank: 22,
    miles_per_gallon: 6.8, fuel_consumed_gallons: 1835.3,
    idle_hours: 41.5, run_hours: 312.7,
    dvir_completion_rate: 94.0, pre_trip_dvir_count: 62,
    post_trip_dvir_count: 58, total_dvir_count: 120,
    customer_requests: 3, customer_requests_per_10k_miles: 2.4,
    kudos: 2, kudos_per_10k_miles: 1.6,
    bus_days: 38, total_miles: 11920.6, weekend_availability_percentage: 62.5,
    dispatch_count: 44,
  },
  {
    compiled_score: 74, compiled_score_tier: 'Fair',
    employment_status: 'Active', date_employment_start: '2022-09-06',
    miles_driven: 9875.2, date_range_days: 90,
    risk_score: 79, risk_score_rank: 38,
    speeding_percentage: 2.86, distracted_driving_percentage: 1.7,
    following_distance_per_10k_miles: 5.4, harsh_braking_per_10k_miles: 3.6,
    idling_percentage: 13.2, idling_rank: 45,
    miles_per_gallon: 6.1, fuel_consumed_gallons: 1618.9,
    idle_hours: 58.2, run_hours: 288.4,
    dvir_completion_rate: 81.5, pre_trip_dvir_count: 48,
    post_trip_dvir_count: 44, total_dvir_count: 92,
    customer_requests: 7, customer_requests_per_10k_miles: 7.1,
    kudos: 0, kudos_per_10k_miles: 0,
    bus_days: 31, total_miles: 9240.1, weekend_availability_percentage: 41.0,
    dispatch_count: 35,
  },
]
import DriverManagerCompareHelp from './driver_manager_compare_help.vue'

export default {
  name: "driver_manager_compare",

  components: { DriverManagerCompareHelp },

  mixins: [dtPickerPortal],

  props: {
    // FI-2064: Compare is now always a standalone top-level page (embedded=false),
    // reached via its own nav entry/route. The prop is kept only so an `embedded`
    // usage isn't a hard break if anything still passes it.
    embedded: { type: Boolean, default: false },
    // When embedded, the dashboard's own top-of-page date range (shared with the
    // Fleet Overview/Individual tabs) drives this tab too, instead of duplicating a
    // second, disconnected date picker — see the details_fromDate/details_toDate
    // watchers below for how these sync into local state.
    fromDate: { type: String, default: null },
    toDate: { type: String, default: null },
  },

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    min_driver_slots() {
      return MIN_DRIVER_SLOTS
    },
    isCmTenant() {
      return (this.user && this.user.types && this.user.types[this.user.tenant]) === 'CM'
    },
    hasKudos() {
      return tenantScorecardHasKudos(this.user)
    },
    fleet_avg_selected() {
      return this.driver_selections.includes('__fleet_avg__')
    },
    quickRangeOptions() {
      return QUICK_RANGE_MONTHS
    },
    activeQuickRangeMonths() {
      return activeQuickRangeMonths(
        { from: this.details_fromDate, to: this.details_toDate })
    },
    optionsFrom() {
      return scorecardPickerOptions('from')
    },
    optionsTo() {
      return scorecardPickerOptions('to')
    },
    valid_driver_ids() {
      // Deduped, in slot order: two slots pointing at the same driver are one
      // fetch and one column, not two.
      const seen = new Set()
      return this.driver_selections.filter(id => {
        if (id === null || seen.has(id)) return false
        seen.add(id)
        return true
      })
    },
    driver_name_by_id() {
      return this.driver_options.reduce((acc, d) => {
        acc[d.value] = d.text
        return acc
      }, {})
    },
    // Each driver is fetched on its own the moment it's picked, so the table is
    // whatever has landed so far, ordered by slot -- columns appear one at a
    // time instead of waiting on a single all-or-nothing load.
    scorecards() {
      return this.valid_driver_ids.map(id => this.scorecard_cache[id]).filter(Boolean)
    },
    status_filter_options() {
      const statuses = [...new Set(
        this.all_drivers
          .map(d => d.employmentStatus)
          .filter(s => s && s.trim())
      )].sort()
      return [
        { value: null, text: 'All Statuses' },
        ...statuses.map(s => ({ value: s, text: s })),
      ]
    },
    filtered_driver_options() {
      if (!this.selected_status_filter) return this.driver_options
      const allowed = new Set(
        this.all_drivers
          .filter(d => d.employmentStatus === this.selected_status_filter)
          .map(d => String(d.value))
      )
      return this.driver_options.filter(o => o.value === '__fleet_avg__' || allowed.has(String(o.value)))
    },
    available_driver_count() {
      // Excludes the Fleet Average pseudo-option -- this is a count of real
      // drivers currently offered by the dropdowns, i.e. what the Status filter
      // at the top of the page has narrowed them to.
      return this.filtered_driver_options.filter(o => o.value !== '__fleet_avg__').length
    },
    // One entry per slot (not per unique driver): the columns line up with the
    // dropdowns above them, so two slots holding the same driver stay two
    // columns rather than silently collapsing into one.
    display_columns() {
      return this.driver_selections.map((driver_id, idx) => {
        const entry = driver_id ? this.scorecard_cache[driver_id] : null
        let state = 'sample'
        if (driver_id) {
          if (entry) state = 'loaded'
          else if (this.has_failed(driver_id)) state = 'failed'
          else state = 'loading'
        }
        return {
          idx,
          driver_id,
          state,
          data: entry ? entry.data : SAMPLE_SCORECARDS[idx % SAMPLE_SCORECARDS.length],
        }
      })
    },
    table_min_width() {
      // Two or three columns fit the page; past that the wrapper scrolls rather
      // than crushing every column narrower than its numbers.
      return (LABEL_COL_PX + ADD_COL_PX + this.display_columns.length * MIN_DRIVER_COL_PX) + 'px'
    },
    mergedThresholds() {
      return mergeThresholds(this.scorecard_thresholds)
    },
    results_match_selections() {
      // Gates the CSV button: only offer the export once every picked driver has
      // actually landed, so a partly-loaded board can't produce a partial file.
      return this.valid_driver_ids.length > 0 && this.scorecards.length === this.valid_driver_ids.length
    },
    metric_rows() {
      const n = (val, dec = 2) => this.formatNumber(val, dec)
      // Deliberately includes the samples: if this flag flipped when the first
      // real scorecard landed, the Combined Score section would appear or vanish
      // mid-session and the table would change shape -- the one thing this
      // layout promises it won't do.
      const hasCompiledScore = SAMPLE_SCORECARDS.some(d => d.compiled_score !== null && d.compiled_score !== undefined)
        || this.scorecards.some(sc => sc.data.compiled_score !== null && sc.data.compiled_score !== undefined)
      const compiledRows = hasCompiledScore ? [
        { key: 'sec_compiled', label: 'Combined Score', section: true },
        { key: 'compiled_score', label: 'Combined', format: d => d.compiled_score !== null && d.compiled_score !== undefined ? d.compiled_score : 'N/A', cssClass: d => this.getCompiledScoreClass(d.compiled_score, this.getCompiledScoreTier(d)) },
        { key: 'compiled_tier', label: 'Tier', format: d => this.getCompiledScoreTier(d) || 'N/A', cssClass: d => tierClass(this.getCompiledScoreTier(d), 'text') },
      ] : []
      return [
        ...compiledRows,
        ...(!this.isCmTenant ? [
          { key: 'sec_driver_info', label: 'Driver Info', section: true },
          { key: 'employment_status', label: 'Status', format: d => d.employment_status || '—' },
          { key: 'hire_date', label: 'Hire Date', format: d => this.formatHireDate(d.date_employment_start) || '—' },
          { key: 'tenure', label: 'Tenure', format: d => this.tenureString(d.date_employment_start) || '—' },
        ] : []),
        { key: 'sec_general', label: 'General', section: true },
        { key: 'miles_driven', label: 'Miles Driven', format: d => n(d.miles_driven || d.distance_traveled_miles, 2) },
        { key: 'date_range_days', label: 'Date Range Days', format: d => d.date_range_days || 0 },
        { key: 'sec_risk', label: 'Safety Metrics', section: true },
        { key: 'risk_score', label: 'Safety Score', format: d => d.risk_score || 'N/A', cssClass: d => getFieldClass('risk_score', d.risk_score, this.mergedThresholds) },
        { key: 'risk_rank', label: 'Safety Rank', format: d => d.risk_score_rank || 'N/A', cssClass: d => getFieldClass('risk_rank', d.risk_score_rank, this.mergedThresholds, { total_drivers: this.total_drivers }) },
        { key: 'speeding_pct', label: 'Speeding %', format: d => n(d.speeding_percentage, 2) + '%', cssClass: d => getFieldClass('speeding_percentage', d.speeding_percentage, this.mergedThresholds) },
        { key: 'distracted', label: 'Distracted / 10k', format: d => n(d.distracted_driving_percentage, 2), cssClass: d => getFieldClass('distracted_driving_percentage', d.distracted_driving_percentage, this.mergedThresholds) },
        { key: 'following_dist', label: 'Following Dist / 10k', format: d => n(d.following_distance_per_10k_miles, 2), cssClass: d => getFieldClass('following_distance_per_10k_miles', d.following_distance_per_10k_miles, this.mergedThresholds) },
        { key: 'harsh_braking', label: 'Harsh Braking / 10k', format: d => n(d.harsh_braking_per_10k_miles, 2), cssClass: d => getFieldClass('harsh_braking_per_10k_miles', d.harsh_braking_per_10k_miles, this.mergedThresholds) },
        { key: 'sec_idle', label: 'Idling & Fuel', section: true },
        { key: 'idling_pct', label: 'Idling %', format: d => n(d.idling_percentage, 2) + '%', cssClass: d => getFieldClass('idling_percentage', d.idling_percentage, this.mergedThresholds) },
        { key: 'idling_rank', label: 'Idling Rank', format: d => d.idling_rank || 'N/A', cssClass: d => getFieldClass('idling_rank', d.idling_rank, this.mergedThresholds, { total_drivers: this.total_drivers }) },
        { key: 'mpg', label: 'MPG', format: d => n(d.miles_per_gallon, 2) || 'N/A', cssClass: d => d.miles_per_gallon == null ? 'text-danger' : getFieldClass('miles_per_gallon', d.miles_per_gallon, this.mergedThresholds) },
        { key: 'gallons', label: 'Gallons', format: d => n(d.fuel_consumed_gallons, 2), cssClass: d => getFieldClass('fuel_consumed_gallons', d.fuel_consumed_gallons, this.mergedThresholds) },
        { key: 'idle_hrs', label: 'Idle Hrs', format: d => n(d.idle_hours, 2), cssClass: d => getFieldClass('idle_hours', d.idle_hours, this.mergedThresholds) },
        { key: 'run_hrs', label: 'Run Hrs', format: d => n(d.run_hours, 2) },
        { key: 'sec_dvir', label: 'DVIR (Vehicle Inspections)', section: true },
        { key: 'dvir_rate', label: 'DVIRs / (Bus Days × 2)', format: d => d.dvir_completion_rate !== null && d.dvir_completion_rate !== undefined ? n(d.dvir_completion_rate, 1) + '%' : 'N/A', cssClass: d => getFieldClass('dvir_completion_rate', d.dvir_completion_rate, this.mergedThresholds) },
        { key: 'pre_trip_dvir', label: 'Pre-Trip DVIRs', format: d => d.pre_trip_dvir_count || 0 },
        { key: 'post_trip_dvir', label: 'Post-Trip DVIRs', format: d => d.post_trip_dvir_count || 0 },
        { key: 'total_dvir', label: 'Total DVIRs', format: d => d.total_dvir_count || 0 },
        { key: 'sec_dispatch', label: this.isCmTenant ? 'Dispatch (CM)' : 'Dispatch (TBN)', section: true },
        ...(!this.isCmTenant ? [
          { key: 'cust_req', label: 'Customer Requests', format: d => d.customer_requests || 0, cssClass: d => getFieldClass('customer_requests', d.customer_requests || 0, this.mergedThresholds) },
          { key: 'cust_req_10k', label: 'Cust Req / 10k', format: d => n(d.customer_requests_per_10k_miles, 2), cssClass: d => getFieldClass('customer_requests_per_10k_miles', d.customer_requests_per_10k_miles || 0, this.mergedThresholds) },
        ] : []),
        { key: 'bus_days', label: 'Bus Days', format: d => d.bus_days || 0, cssClass: d => getFieldClass('bus_days', d.bus_days, this.mergedThresholds) },
        ...(!this.isCmTenant ? [
          { key: 'dispatch_miles', label: 'Dispatch Miles', format: d => n(d.total_miles, 2) },
          { key: 'weekend_avail', label: 'Weekend Avail %', format: d => n(d.weekend_availability_percentage, 2) + '%', cssClass: d => getFieldClass('weekend_availability_percentage', d.weekend_availability_percentage, this.mergedThresholds) },
        ] : []),
        ...(this.isCmTenant ? [
          { key: 'dispatch_count', label: 'Dispatch Count', format: d => d.dispatch_count || 0 },
        ] : []),
        // FI-2217: customer kudos from ERM, its own section since it isn't dispatch data
        ...(this.hasKudos ? [
          { key: 'sec_kudos', label: 'Kudos (ERM)', section: true },
          { key: 'kudos', label: 'Kudos', format: d => d.kudos == null ? 'N/A' : d.kudos },
          { key: 'kudos_10k', label: 'Kudos / 10k', format: d => d.kudos_per_10k_miles == null ? 'N/A' : n(d.kudos_per_10k_miles, 2), cssClass: d => getFieldClass('kudos_per_10k_miles', d.kudos_per_10k_miles, this.mergedThresholds) },
        ] : []),
      ]
    }
  },

  data() {
    return {
      downloading_csv: false,
      driver_selections: Array(MIN_DRIVER_SLOTS).fill(null),
      driver_options: [],
      all_drivers: [],
      selected_status_filter: null,
      total_drivers: 0,
      // driver_id -> { driver_id, driver_name, data }, for the current date
      // range only; invalidate_scorecards() empties it when the range moves.
      scorecard_cache: {},
      loading_ids: [],
      failed_ids: [],
      scorecard_thresholds: null,
      details_fromDate: this.fromDate || justDate(defaultScorecardRange().from),
      details_toDate: this.toDate || justDate(defaultScorecardRange().to),
    }
  },

  created() {
    // Bumped by invalidate_scorecards(); a fetch that resolves against an older
    // generation is discarded rather than writing into the new range's state.
    this._gen = 0
    this.load_drivers()
  },

  watch: {
    // Keep local state in sync when the dashboard's shared date range changes (e.g.
    // the user moved the range on the Fleet Overview tab, then switched to Compare).
    // Only relevant when embedded — standalone usage never receives these props.
    fromDate(val) {
      if (val && val !== this.details_fromDate) this.details_fromDate = val
    },
    toDate(val) {
      if (val && val !== this.details_toDate) this.details_toDate = val
    },
    details_toDate() {
      const fixed = normalizeRange(
        { from: this.details_fromDate, to: this.details_toDate },
        { changed: 'to' })
      // null = unparseable (cleared or half-typed): leave it alone and do not
      // reload, or "Invalid date" reaches the api as a month string
      if (!fixed) return
      if (fixed.from !== this.details_fromDate) this.details_fromDate = fixed.from
      if (fixed.to !== this.details_toDate) this.details_toDate = fixed.to
      this.reload_if_already_loaded()
    },
    details_fromDate() {
      const fixed = normalizeRange(
        { from: this.details_fromDate, to: this.details_toDate },
        { changed: 'from' })
      // null = unparseable (cleared or half-typed): leave it alone and do not
      // reload, or "Invalid date" reaches the api as a month string
      if (!fixed) return
      if (fixed.from !== this.details_fromDate) this.details_fromDate = fixed.from
      if (fixed.to !== this.details_toDate) this.details_toDate = fixed.to
      this.reload_if_already_loaded()
    },
  },

  methods: {
    async load_drivers(skipCacheBust = false, fallbackDrivers = null) {
      try {
        const response = this.isCmTenant
          ? await cm_get_drivers_scorecard_list(this.user.tenant)
          : await tbn_get_drivers_scorecard_list(this.user.tenant)
        const { value: { data } } = response
        let drivers = []
        if (Array.isArray(data)) {
          drivers = data
        } else if (data && data.data && Array.isArray(data.data)) {
          drivers = data.data
        }
        let filteredDrivers = drivers.filter(driver => driver.value && driver.value !== "")
        // Auto-bust stale cache if employmentStatus or dateEmploymentStart is missing (TBN only)
        if (!this.isCmTenant && !skipCacheBust && filteredDrivers.length > 0 && (!filteredDrivers.some(d => d.employmentStatus) || !filteredDrivers.some(d => d.dateEmploymentStart))) {
          try { await tbn_clear_scorecard_driver_list_cache(this.user.tenant) } catch (_) {}
          // Hand the pre-bust roster to the retry: clearing the cache is only a
          // win if the rebuild produces something. A tenant whose roster lives
          // in that cache as a seeded fixture (demo tenants, which have no
          // tbn_enterprise_id to rebuild from) would otherwise trade a slightly
          // stale list for a permanently empty one.
          return this.load_drivers(true, filteredDrivers)
        }
        if (filteredDrivers.length === 0 && fallbackDrivers && fallbackDrivers.length > 0) {
          filteredDrivers = fallbackDrivers
        }
        const sorted = [...filteredDrivers].sort((a, b) => {
          const textA = (a.text || a.fullName || a.name || "").toLowerCase()
          const textB = (b.text || b.fullName || b.name || "").toLowerCase()
          return textA.localeCompare(textB)
        })
        this.all_drivers = sorted
        this.total_drivers = sorted.length
        this.driver_options = [
          { value: '__fleet_avg__', text: 'Fleet Average' },
          ...sorted.map(driver => ({
            value: driver.value,
            text: driver.text || driver.fullName || driver.name || `${driver.firstName || ""} ${driver.lastName || ""}`.trim()
          }))
        ]
      } catch (error) {
        console.error("Error loading drivers:", error)
        this.driver_options = []
      }
    },


    add_driver() {
      this.driver_selections.push(null)
    },

    remove_driver(idx) {
      if (this.driver_selections.length <= MIN_DRIVER_SLOTS) return
      this.driver_selections.splice(idx, 1)
    },

    // a chip writes its range into the pickers, which are always visible and
    // show exactly the days the page reads
    setDatePreset(months) {
      const { from, to } = quickRangeDates(months)
      this.details_fromDate = from
      this.details_toDate = to
    },

    // Called from the date watchers so that changing the range (or the shared
    // range on another tab when embedded) transparently refreshes what's on
    // screen instead of leaving stale numbers under the new dates.
    // Debounced onto the next tick: a range *shift* moves both details_fromDate
    // and details_toDate in the same tick, firing both watchers -- without this
    // the two would each kick off a full refetch of every selected driver.
    reload_if_already_loaded() {
      if (this._reloadQueued) return
      this._reloadQueued = true
      this.$nextTick(() => {
        this._reloadQueued = false
        this.invalidate_scorecards()
        this.refresh_all_scorecards()
      })
    },

    // The cache only ever holds results for the current date range, so moving
    // the range throws all of it away. Bumping the generation makes any fetch
    // still in flight for the old range a no-op when it resolves.
    invalidate_scorecards() {
      this._gen = (this._gen || 0) + 1
      this.scorecard_cache = {}
      this.loading_ids = []
      this.failed_ids = []
      this.scorecard_thresholds = null
    },

    refresh_all_scorecards() {
      this.valid_driver_ids.forEach(id => this.fetch_scorecard(id))
    },

    is_loading(driver_id) {
      return this.loading_ids.includes(driver_id)
    },

    has_failed(driver_id) {
      return this.failed_ids.includes(driver_id)
    },

    retry_scorecard(driver_id) {
      this.failed_ids = this.failed_ids.filter(id => id !== driver_id)
      this.fetch_scorecard(driver_id)
    },

    // One driver, one request, fired the moment that driver is picked. Already
    // cached or already in flight is a no-op, so re-picking a driver you removed
    // earlier costs nothing.
    async fetch_scorecard(driver_id) {
      if (!driver_id) return
      if (this.scorecard_cache[driver_id] || this.is_loading(driver_id)) return

      const gen = this._gen
      const start_month = moment(this.details_fromDate, "MM-DD-YYYY").format("YYYY-MM-DD")
      const end_month = moment(this.details_toDate, "MM-DD-YYYY").format("YYYY-MM-DD")

      this.loading_ids = [...this.loading_ids, driver_id]
      this.failed_ids = this.failed_ids.filter(id => id !== driver_id)

      try {
        const request = driver_id === '__fleet_avg__'
          ? (this.isCmTenant ? cm_get_fleet_scorecard(this.user.tenant, start_month, end_month) : get_fleet_scorecard(this.user.tenant, start_month, end_month))
          : (this.isCmTenant ? cm_get_driver_scorecard(this.user.tenant, driver_id, start_month, end_month) : get_driver_scorecard(this.user.tenant, driver_id, start_month, end_month))
        const response = await request
        if (gen !== this._gen) return  // the date range moved on; this result is for the old one

        const responseData = (response && response.value && response.value.data) || (response && response.data)
        if (!responseData || responseData.error || !responseData.results) {
          this.failed_ids = [...this.failed_ids, driver_id]
          return
        }

        // Thresholds are per-tenant, identical across every driver in the
        // comparison, so the first response that carries them wins.
        if (!this.scorecard_thresholds && responseData.scorecard_thresholds) {
          this.scorecard_thresholds = responseData.scorecard_thresholds
        }

        this.$set(this.scorecard_cache, driver_id, {
          driver_id,
          driver_name: driver_id === '__fleet_avg__' ? 'Fleet Average' : (this.driver_name_by_id[driver_id] || ""),
          data: responseData.results,
        })
      } catch (error) {
        if (gen !== this._gen) return
        this.failed_ids = [...this.failed_ids, driver_id]
      } finally {
        // A stale generation's state was already wiped by invalidate_scorecards();
        // leave the new generation's loading_ids alone.
        if (gen === this._gen) {
          this.loading_ids = this.loading_ids.filter(id => id !== driver_id)
        }
      }
    },

    async download_csv() {
      this.downloading_csv = true
      const start_month = moment(this.details_fromDate, "MM-DD-YYYY").format("YYYY-MM-DD")
      const end_month = moment(this.details_toDate, "MM-DD-YYYY").format("YYYY-MM-DD")
      const driver_ids = this.scorecards.map(sc => sc.driver_id).join(',')
      const driver_names = this.scorecards.map(sc => sc.driver_name || '').join('|')
      try {
        const csvUrl = this.isCmTenant
          ? `${cm_api_url}bookings/driver-scorecard/comparison/csv?tenant=${this.user.tenant}&start_month=${start_month}&end_month=${end_month}&driver_ids=${encodeURIComponent(driver_ids)}&driver_names=${encodeURIComponent(driver_names)}`
          : `${tbn_api_url}tbn/driver-scorecard/comparison/csv?tenant=${this.user.tenant}&start_month=${start_month}&end_month=${end_month}&driver_ids=${encodeURIComponent(driver_ids)}&driver_names=${encodeURIComponent(driver_names)}`
        const response = await axios.get(
          csvUrl,
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` }, responseType: 'blob' }
        )
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const prefix = this.isCmTenant ? 'cm-driver-scorecard-comparison' : 'driver-scorecard-comparison'
        link.setAttribute('download', `${prefix}-${start_month}-to-${end_month}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        this.$bvToast.toast('Download complete.', { variant: 'success', autoHideDelay: 3000 })
      } catch (e) {
        this.$bvToast.toast('Could not download comparison data.', { variant: 'danger' })
      } finally {
        this.downloading_csv = false
      }
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined) return "—"
      return Number(value).toFixed(decimals)
    },

    getCompiledScoreTier(d) {
      if (!d) return ''
      const score = d.compiled_score
      if (score === null || score === undefined) return ''
      // The backend is the source of truth for the tier: it's the only place that
      // knows about Insufficient Data. Recomputing from a raw elite/solid/coaching
      // cfg here (as this used to) would silently mislabel those drivers.
      if (d.compiled_score_tier) return d.compiled_score_tier
      const cfg = this.mergedThresholds.compiled_score
      const s = Number(score)
      if (cfg) {
        if (s >= Number(cfg.elite))    return 'Elite'
        if (s >= Number(cfg.solid))    return 'Solid'
        if (s >= Number(cfg.coaching)) return 'Fair'
        return 'High Risk'
      }
      return ''
    },

    getCompiledScoreClass(score, tier) {
      return getCompiledScoreComparisonClass(score, this.mergedThresholds.compiled_score, tier)
    },

    formatHireDate(dateStr) {
      if (!dateStr) return ''
      const m = moment(dateStr, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true)
      if (m.isValid()) return m.format('MMM D, YYYY')
      const mFallback = moment(dateStr)
      return mFallback.isValid() ? mFallback.format('MMM D, YYYY') : dateStr
    },

    tenureString(dateStr) {
      if (!dateStr) return ''
      let start = moment(dateStr, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true)
      if (!start.isValid()) start = moment(dateStr)
      if (!start.isValid()) return ''
      const now = moment()
      const years = now.diff(start, 'years')
      const months = now.diff(start.clone().add(years, 'years'), 'months')
      if (years === 0 && months === 0) return 'Less than 1 month'
      const parts = []
      if (years > 0) parts.push(`${years} yr${years !== 1 ? 's' : ''}`)
      if (months > 0) parts.push(`${months} mo`)
      return parts.join(', ')
    }
  }
}
</script>

<style scoped>
/* date preset chips (od-chip recipe, token-native so midnight needs no
   overrides) --
   kept in sync with the identical block in driver_manager_dashboard.vue /
   driver_manager_individual.vue since scoped styles don't share across
   components */
.dsd-date-presets {
  gap: 6px;
}
.sc-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
/* :not(.sc-chip--active) is load-bearing: .sc-chip:hover out-specifies
   .sc-chip--active (a pseudo-class beats nothing at the same class count), so a
   bare hover rule repainted the SELECTED chip near-white while its own
   color: #fff survived -- white on white for as long as the pointer stayed on
   the chip you just clicked, in every theme. */
.sc-chip:not(.sc-chip--active):hover {
  background: var(--fi-surface-sunken, #eef4fb);
}
.sc-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.sc-chip--active:hover {
  background: var(--fi-ops-accent-strong, #305b80);
  border-color: var(--fi-ops-accent-strong, #305b80);
}
.dsd-date-fields {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dsd-date-field {
  width: 122px;
  font-size: 0.9rem;
}
/* ios safari zooms the page on focusing any input under 16px */
@media (max-width: 768px) {
  .dsd-date-field {
    font-size: 16px;
  }
}
.dsd-date-sep {
  color: var(--fi-text-muted, #6c757d);
  font-size: 0.85rem;
}

.driver-picker-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0;
}
.driver-picker-status {
  display: flex;
  align-items: center;
}
.dm-board-loading {
  text-align: center;
  margin: 50px;
}
/* Fixed layout is the whole trick: the colgroup owns the column widths, so a
   cell swapping a sample value for a real one can never widen its column and
   shove the rest of the table sideways. */
.dm-board-table {
  table-layout: fixed;
}
/* .comparison-table is declared further down and would otherwise win on source
   order; these carry the extra class to out-specify it rather than depend on
   where in the file they sit. */
.dm-board-table.comparison-table {
  width: 100%;
  min-width: 0;
}
/* the shared table styles pin every driver column to 180px, which under a fixed
   layout would stop them sharing the page width; the colgroup sizes them here */
.dm-board-table .comparison-driver-col {
  width: auto;
  min-width: 0;
}
.dm-board-table .comparison-label-col,
.dm-board-table .comparison-label-cell {
  width: 180px;
  min-width: 180px;
}
.dm-col-labels {
  width: 180px;
}
.dm-col-add {
  width: 56px;
}
.dm-head-cell {
  vertical-align: bottom;
  padding: 8px 6px !important;
}
.dm-col-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
}
.dm-col-index {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 1.05rem;
  font-weight: 700;
  /* tight spacing suits the larger size -- 0.06em was sized for a small eyebrow */
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--fi-text, #2c2c2a);
}
.dm-col-remove {
  border: 0;
  background: none;
  padding: 0 2px;
  line-height: 1;
  font-size: 1.3rem;
  color: var(--fi-text-faint, #74726c);
  cursor: pointer;
}
.dm-col-remove:hover {
  color: var(--fi-danger, #b91c1c);
}
.scorecard-driver-select {
  width: 100%;
  font-weight: 600;
}
.dm-col-hint {
  min-height: 1.1rem;
  font-size: 0.68rem;
  font-weight: 400;
  color: var(--fi-text-muted, #5c626e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.dm-col-retry {
  margin-left: 4px;
  font-weight: 600;
}

/* sample and pending values: present so the table keeps its shape, played down
   so they never read as this driver's numbers */
.dm-cell-muted {
  color: var(--fi-text-faint, #74726c);
  opacity: 0.55;
  font-style: italic;
}

/* the add column is a gutter, not a data column -- no rules, no fill */
.dm-add-col {
  border: 0 !important;
  background: transparent !important;
  padding: 4px 0 !important;
  text-align: center;
}
.dm-add-btn {
  width: 40px;
  height: 40px;
  border: 1px dashed var(--fi-line-strong, #d3d1c7);
  border-radius: var(--fi-radius-md, 8px);
  background: var(--fi-surface-sunken, #f7f8fa);
  color: var(--fi-ops-accent, #3a6e99);
  font-size: 1.6rem;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  transition: background .15s, border-color .15s, color .15s;
}
.dm-add-btn:hover {
  background: var(--fi-brand-tint, #e9f3fc);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: var(--fi-ops-accent-strong, #305b80);
}

.comparison-scroll-hint {
  display: none;
  text-align: center;
  font-size: 0.78rem;
  color: #888;
  margin-bottom: 4px;
}

.comparison-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 8px;
}
.comparison-table {
  border-collapse: collapse;
  width: auto;
  min-width: 100%;
  font-size: 0.95rem;
  line-height: 1.35;
}
.comparison-table th,
.comparison-table td {
  border: 1px solid #dee2e6;
  padding: 6px 10px;
  white-space: nowrap;
}
.comparison-label-col {
  width: 140px;
  min-width: 140px;
  background: #f8f9fa;
  position: sticky;
  left: 0;
  z-index: 2;
}
.comparison-driver-col {
  min-width: 180px;
  width: 180px;
  text-align: center;
  vertical-align: bottom;
  padding: 6px 4px !important;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 1;
}
.comparison-label-cell {
  background: #f8f9fa;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 1;
}
.comparison-label-cell.section-label {
  font-weight: 700;
  font-size: 0.98rem;
  background: #e2e6ea;
  color: #333;
}
.comparison-value-cell {
  text-align: center;
  font-variant-numeric: tabular-nums;
  font-size: 1.02rem;
}
tr.section-header td {
  background: #e2e6ea;
}
.fleet-avg-col {
  background: #fff8e1 !important;
  font-weight: 600;
}
tr.section-header .fleet-avg-col {
  background: #ffe082 !important;
}
@media (max-width: 768px) {
  /* the fixed layout keeps its proportions on a phone -- the wrapper just
     scrolls, since 180px of labels plus two 200px columns won't fit */
  .dm-board-table .comparison-label-col,
  .dm-board-table .comparison-label-cell {
    width: 140px;
    min-width: 140px;
  }
  .dm-col-labels {
    width: 140px;
  }
  .scorecard-driver-select {
    /* ios safari zooms the page on focusing any input under 16px */
    font-size: 16px;
  }
  .comparison-scroll-hint {
    display: block;
  }
  .driver-picker-strip {
    justify-content: center;
  }
  .driver-picker-status {
    width: 100%;
    justify-content: center;
  }
}
</style>
